import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import i18next from 'i18next';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { Card, Col, Form, Row, Container } from 'react-bootstrap';
import { useApiClient } from '../../hooks/useApiClient';
import toaster from '../../utils/toaster';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import CreatableSelectExtended from '../../components/CreatableSelectExtended/CreatableSelectExtended';
import Btn from '../../components/Btn/Btn';
import {
  ApiError,
  VehicleUpdateRequestDTO,
  OptionalDTO,
} from '../../api';
import { emptyStringsToNull } from 'src/motornet/common/utils';
import type {
  PlatformVehicleImportRequest,
  PlatformVehicleImportDetail,
  PlatformVehicleImportResponse,
} from 'src/motornet/types';
import { GTMsendEvent } from '../../utils/trackingGA4';
import OptionalsCard from '../../components/OptionalsCard/OptionalsCard';
import { cloneDeep } from 'lodash';
import { NOLEGGIATORI_QUOTATORE } from '../../utils/constants';
import MarketFilter from 'src/components/MarketFilter/MarketFilter';
import { Markets } from 'src/types';
import ToolTip from '../../components/Tooltip/Tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import { isMarket } from '../../utils/markets';
import { arrayToOptions, isInvalidNumber, stringToOption } from '../../utils';

import './DetailsFormPage.scss';
import _ from 'lodash';
import VehicleRentalCompanies from 'src/components/VehicleRentalCompanies/VehicleRentalCompanies';
import { useErrorToast } from 'src/hooks/useErrorToast';

type PageMode = 'edit' | 'clone';

const deducePageMode = (pathname: string): PageMode =>
  pathname.split('/').pop() as PageMode;

const newOptional: OptionalDTO = {
  codice_costruttore: '',
  descrizione: '',
  valore: 0,
};

const listaNoleggiatoriQuotatore = [
  { noleggiatore: NOLEGGIATORI_QUOTATORE.arval.name, is_checked: true, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.ayvens.name, is_checked: true, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.leasys.name, is_checked: true, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.athlon.name, is_checked: true, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.alphabet.name, is_checked: true, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.drivalia.name, is_checked: true, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.kinto.name, is_checked: false, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.vw_leasing.name, is_checked: true, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.hyundai_rent.name, is_checked: true, extra_sconto_perc: null },
];


interface SelectOptions {
  value: string,
  label: string;
}

const salableByOptions: any[] = [
  {
    value: 'MASERATI',
    label: 'MASERATI',
  },
  {
    value: 'VOLKSWAGEN',
    label: 'VOLKSWAGEN',
  },
  {
    value: 'STELLANTIS',
    label: 'STELLANTIS',
  },
  // {
  //   value: 'FACILE',
  //   label: 'FACILE',
  // },
];

const DetailsFormPage = () => {
  const { id } = useParams<{ id: string; }>();
  const { pathname } = useLocation();
  const { apiClient, handleApi } = useApiClient();
  const { t } = useTranslation();
  const pageMode = deducePageMode(pathname);

  const [addOptionDisabled, setAddOptionDisabled] = useState(false);
  const [initialValues, setInitialValues] = useState(null as any);
  const [loading, setLoading] = useState(true);
  const [externalWarnings, setExternalWarnings] = useState<any>(null);
  const [externalErrors, setExternalErrors] = useState<any>(null);
  const [pageTitle, setPageTitle] = useState('');
  const [vehicleName, setVehicleName] = useState('');
  const [dealerOptions, setDealerOptions] = useState<any[]>([]);
  const navigate = useNavigate();
  const { showError } = useErrorToast()

  useEffect(() => {
    handleApi(apiClient.dealerController.getRegisterDealers())
      .then((res) =>
        setDealerOptions(res.map((r) => stringToOption(r.ragione_sociale)))
      )
      .catch((err) => { });
  }, []);

  useEffect(() => {
    // EDIT or CLONE — vehicle creation lives in MotornetCreateVehiclePage (/vehicles/create)
    if (pageMode === 'edit') setPageTitle(i18next.t('modify_vehicle'));
    if (pageMode === 'clone') setPageTitle(i18next.t('duplicate_vehicle'));

    handleApi(apiClient.vehicleController.getVehicles1(id!))
      .then((res) => {
        setVehicleName(
          `${res.dati_veicolo?.marca} ${res.dati_veicolo?.modello}`
        );

        res.dettagli_veicolo.limited_visibility = res.dettagli_veicolo?.limited_visibility || []
        let listaNoleggiatoriToUpdate = cloneDeep(listaNoleggiatoriQuotatore);
        listaNoleggiatoriToUpdate.forEach(el => {
          const noleggiatore = el.noleggiatore;
          const nres = res.lista_noleggiatori_quotatore.find(n => n.noleggiatore === noleggiatore);

          if (nres) {
            el.is_checked = nres.is_checked;
            el.extra_sconto_perc = nres.extra_sconto_perc;
          }
        });

        let limited_visibility
        let dealerValue = res.dettagli_veicolo?.dealer ?? '';

        // handle limited visibility which is an array of strings
        if (!_.isEmpty(res?.dettagli_veicolo?.limited_visibility)) {
          limited_visibility = arrayToOptions(res.dettagli_veicolo?.limited_visibility)
        }

        const initialVals: any = {
          dealer: dealerValue,
          codice_costruttore: res.dettagli_veicolo?.codice_costruttore,
          settimana_prod: res.dettagli_veicolo?.settimana_prod ?? '',
          data_disp: res.dettagli_veicolo?.data_disp ?? '',
          city: res.dettagli_veicolo?.city ?? '',
          data_telaio: res.dettagli_veicolo?.data_telaio ?? '',
          telaio: res.dettagli_veicolo?.telaio ?? '',
          data_immatricolazione: res.dettagli_veicolo?.data_immatricolazione ?? '',
          targa: res.dettagli_veicolo?.targa ?? '',
          id_bo: res.dettagli_veicolo?.id_bo ?? '',
          giorno_disp_horizon: res.dettagli_veicolo?.giorno_disp ?? '',
          promo: res.dettagli_veicolo?.promo ?? false,
          promo_note: res.dettagli_veicolo?.promo_note ?? '',
          note: res.dettagli_veicolo?.note ?? '',
          codice_colore_esterno: res.dettagli_veicolo?.codice_colore_esterno ?? '',
          codice_colore_interno: res.dettagli_veicolo?.codice_colore_interno ?? '',
          colore_esterno: res.dettagli_veicolo?.colore_esterno ?? '',
          colore_interno: res.dettagli_veicolo?.colore_interno ?? '',
          optionals: res.optionals?.length ? res.optionals : [newOptional],
          document: res.dettagli_veicolo?.document?.length ? res.dettagli_veicolo?.document : [],
          stato_corrente: res.stato_corrente,
          prezzo_listino: res.dati_veicolo.prezzo_listino,
          lista_noleggiatori_quotatore: listaNoleggiatoriToUpdate,
          extra_sconto_perc: res.dati_veicolo.extra_sconto_perc,
          limited_visibility,
          jato_uid: res.dati_veicolo.jato_uid,
          // Motornet identity (read-only in the form): codice_eurotax + codice_costruttore
          vehicle_code: res.dati_veicolo.vehicle_code ?? '',
          manufacture_code: res.dati_veicolo.manufacture_code ?? '',
        };

        setAddOptionDisabled(!res.optionals?.length);

        // handle nr_ordine VS nr_ordine_list
        if (pageMode === 'edit') {
          // edit mode has a single nr_ordine
          initialVals.nr_ordine = res.dettagli_veicolo?.nr_ordine ?? '';
        }
        if (pageMode === 'clone') {
          // when cloning, user must provide new order numbers
          initialVals.nr_ordine_list = [];
        }

        setInitialValues(initialVals);
        setLoading(false);
      })
      .catch((error) => { });
  }, []);

  const validateListaNoleggiatoriQuotatore = (values: any, errors) => {
    const list = values.lista_noleggiatori_quotatore
    if (!list || !list.length) return;
    list.forEach((item, idx) => {
      if (isInvalidNumber(item?.extra_sconto_perc)) {
        _.set(
          errors,
          `lista_noleggiatori_quotatore.${idx}.extra_sconto_perc`,
          i18next.t('numeric_values_allowed'
          ))
      }
    })
  }

  const validateFn = (values) => {
    const errors: any = {};
    const REQUIRED_MESSAGE = i18next.t('mandatory');

    setAddOptionDisabled(false);

    for (const optional of values.optionals) {
      if (!optional.codice_costruttore) {
        setAddOptionDisabled(true);
        break;
      }
    }

    if (isInvalidNumber(values.extra_sconto_perc)) {
      errors.extra_sconto_perc = i18next.t('numeric_values_allowed');
    }

    validateListaNoleggiatoriQuotatore(values, errors);

    if (!values.dealer) {
      errors.dealer = REQUIRED_MESSAGE;
    }
    if (!values.codice_colore_esterno) {
      errors.codice_colore_esterno = REQUIRED_MESSAGE;
    }
    // Qui non c'e' il listino: obbligatorio solo se il veicolo ne aveva gia' uno
    if (initialValues?.codice_colore_interno && !values.codice_colore_interno) {
      errors.codice_colore_interno = REQUIRED_MESSAGE;
    }
    if (pageMode === 'edit') {
      if (!values.nr_ordine) {
        errors.nr_ordine = REQUIRED_MESSAGE;
      }
    }
    if (pageMode === 'clone') {
      if (!values.nr_ordine_list?.length) {
        errors.nr_ordine_list = REQUIRED_MESSAGE;
      }
    }

    return errors;
  };

  const handleEditBody = (values: any): VehicleUpdateRequestDTO => {
    const clonedValues = cloneDeep(values);

    clonedValues.optionals = clonedValues.optionals?.filter(
      (o: OptionalDTO) => o.codice_costruttore
    );

    clonedValues.limited_visibility = clonedValues.limited_visibility?.map(
      (item: SelectOptions) => item.value
    );

    return clonedValues as VehicleUpdateRequestDTO;
  };

  // Clone creates a brand-new vehicle through the Motornet import endpoint,
  // exactly like MotornetCreateVehiclePage. codice_eurotax is carried on the
  // source vehicle as vehicle_code.
  const buildImportBody = (values: any): PlatformVehicleImportRequest => {
    const clonedValues = cloneDeep(values);

    const seenCodici = new Set<string>();
    clonedValues.optionals = (clonedValues.optionals ?? []).filter(
      (o: OptionalDTO) => {
        if (!o.codice_costruttore || seenCodici.has(o.codice_costruttore))
          return false;
        seenCodici.add(o.codice_costruttore);
        return true;
      }
    );

    clonedValues.limited_visibility = (clonedValues.limited_visibility ?? []).map(
      (item: SelectOptions) => item.value
    );

    const nr_ordine_list: Array<string> = clonedValues.nr_ordine_list ?? [];
    const { visibility_tag, vehicle_code, manufacture_code, ...detail } =
      clonedValues;
    delete (detail as any).nr_ordine_list;
    delete (detail as any).jato_uid;

    const dettaglio_veicolo = emptyStringsToNull({
      ...detail,
      codice_costruttore: manufacture_code || detail.codice_costruttore,
      codice_eurotax: vehicle_code ?? '',
    }) as PlatformVehicleImportDetail;

    return {
      dettaglio_veicolo,
      nr_ordine_list,
      is_checked: true,
      ...(visibility_tag && { visibility_tag }),
    };
  };


  const showWarningToast = (warnings?: Record<string, string>) => {
    if (!warnings) return;

    const hasActiveWarnings = Object.values(warnings).some(Boolean);

    if (hasActiveWarnings) {
      const message = warnings.image_list || i18next.t('there_are_warnings');
      toaster.warning(message);
    }
  };

  const handleApiError = (err: ApiError) => {
    const errorBody = err?.body;
    const errors = errorBody?.errors;
    const warnings = errorBody?.warnings;

    if (errors) setExternalErrors(errors);
    if (warnings) setExternalWarnings(warnings);

    if (!errors && !warnings) {
      showError(err, i18next.t('data_entry_error'))
      return;
    }

    if (errors) {
      const message = errors?.dup_key || i18next.t('data_entry_error');
      toaster.error(message);
    }

    showWarningToast(warnings);
  }

  const onSubmitFn = (values, { setSubmitting }) => {
    setSubmitting(true);
    setExternalErrors(null);
    setExternalWarnings(null);

    if (pageMode === 'clone') {
      apiClient.motornetControllerService
        .importVehicle(buildImportBody(values))
        .then((res: PlatformVehicleImportResponse) => {
          res?.warnings?.forEach((w) => toaster.warning(w.message));
          toaster.success(i18next.t('duplicated_vehicle_success'));
          GTMsendEvent({
            event: 'complete_actions_sale',
            actionType: 'duplica_veicolo',
          });
          navigate('/available-vehicles');
        })
        .catch((err: ApiError) => handleApiError(err))
        .finally(() => setSubmitting(false));
      return;
    }

    // edit
    handleApi(apiClient.vehicleController.putVehicles(id!, handleEditBody(values)))
      .then((res) => {
        if (res.warnings) {
          setExternalWarnings(res.warnings);

          Object.values(res.warnings).some((v: any) => !!v) &&
            toaster.warning(
              res.warnings?.image_list || i18next.t('there_are_warnings')
            );
        }

        if (res.vehicle_entities?.length && res.vehicle_entities[0]?.id) {
          navigate(`/details/${res.vehicle_entities[0]?.id}/edit`);
          navigate(0);
        }

        toaster.success(i18next.t('updated_vehicle_success'));
        GTMsendEvent({
          event: 'complete_actions_sale',
          actionType: 'modifica_dati_veicolo',
        });
      })
      .catch((err: ApiError) => handleApiError(err))
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Container fluid>
      <Breadcrumb
        item1={{ name: i18next.t('vehicles_list'), link: '/available-vehicles' }}
        item2={{ name: i18next.t('vehicle_details'), link: '/details/' + id }}
        activeItem={{
          name: vehicleName,
        }}
      ></Breadcrumb>

      <div className="display-6 my-4">{pageTitle}</div>

      {!loading && initialValues && (
        <Formik
          initialValues={initialValues}
          validate={validateFn}
          onSubmit={onSubmitFn}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (
              <Form>
                <Card className="mt-2">
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t('dealer')}
                            <span className="red-ast">*</span>
                          </Form.Label>
                          <Select
                            menuPosition="fixed"
                            value={stringToOption(values.dealer)}
                            onBlur={handleBlur}
                            onChange={(opt) => {
                              const newDealerValue = opt?.value || '';
                              const previousDealerValue = values.dealer;
                              setFieldValue('dealer', newDealerValue);

                              // Prefill limited_visibility with the selected dealer value
                              // Get the current limited_visibility array
                              if (isMarket(Markets.es)) {
                                let currentLimitedVisibility = values.limited_visibility || [];

                                // If there was a previous dealer value, remove it from limited_visibility
                                if (previousDealerValue && previousDealerValue !== newDealerValue) {
                                  currentLimitedVisibility = currentLimitedVisibility.filter(
                                    (item: SelectOptions) => item.value !== previousDealerValue
                                  );
                                }

                                // If a new dealer is selected, add it to limited_visibility if not already present
                                if (newDealerValue) {
                                  const isAlreadyInLimitedVisibility = currentLimitedVisibility.some(
                                    (item: SelectOptions) => item.value === newDealerValue
                                  );
                                  if (!isAlreadyInLimitedVisibility) {
                                    const newOption = stringToOption(newDealerValue);
                                    currentLimitedVisibility = [...currentLimitedVisibility, newOption];
                                  }
                                }
                                // Update limited_visibility with the modified array
                                setFieldValue('limited_visibility', currentLimitedVisibility);

                              }
                            }}
                            name="dealer"
                            options={dealerOptions}
                            className={
                              'hor-singleselect group-input ' +
                              (errors.dealer && touched.dealer ? 'error-border' : '')
                            }
                          />

                          {errors.dealer && touched.dealer && (
                            <small className="text-danger">
                              {errors.dealer as string}
                            </small>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t('limited_visibility')}
                            {/* <span className="red-ast">*</span> */}
                            <small id="info-die-required" className='text-muted ms-2'>
                              <FaInfoCircle size={18} />
                              <ToolTip
                                anchorId="info-die-required"
                                content={t('limited_visibility_disclaimer')}
                                className="default"
                              />
                            </small>
                          </Form.Label>

                          <Select
                            menuPosition="fixed"
                            onBlur={handleBlur}
                            onChange={(opt) => {
                              setFieldValue('limited_visibility', opt || []);
                            }}
                            value={values.limited_visibility}
                            name="limited_visibility"
                            options={dealerOptions}
                            isMulti={true}
                            className={
                              'hor-singleselect group-input ' +
                              (errors.limited_visibility && touched.limited_visibility
                                ? 'error-border'
                                : '')
                            }
                          />

                          {errors.limited_visibility && touched.limited_visibility && (
                            <small className="text-danger">
                              {errors.limited_visibility as string}
                            </small>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('vehicle_code', 'Codice Veicolo')}</Form.Label>
                          <Form.Control
                            value={values.vehicle_code ?? ''}
                            name="vehicle_code"
                            type="text"
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('builder_code')}</Form.Label>
                          <Form.Control
                            value={values.manufacture_code || values.codice_costruttore || ''}
                            name="manufacture_code"
                            type="text"
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t('external_color_code')}
                            <span className="red-ast">*</span>
                          </Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.codice_colore_esterno}
                            name="codice_colore_esterno"
                            type="text"
                            className={
                              errors.codice_colore_esterno &&
                                touched.codice_colore_esterno
                                ? 'error-border'
                                : externalWarnings?.codice_colore_esterno
                                  ? 'warning-border'
                                  : ''
                            }
                          />
                          {errors.codice_colore_esterno &&
                            touched.codice_colore_esterno && (
                              <small className="text-danger">
                                {errors.codice_colore_esterno as string}
                              </small>
                            )}

                          {externalWarnings?.codice_colore_esterno && (
                            <small className="text-warning">
                              {externalWarnings.codice_colore_esterno}
                            </small>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('external_color_description')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.colore_esterno}
                            name="colore_esterno"
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t('internal_color_code')}
                            {initialValues?.codice_colore_interno && (
                              <span className="red-ast">*</span>
                            )}
                          </Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.codice_colore_interno}
                            name="codice_colore_interno"
                            type="text"
                            className={
                              externalErrors?.codice_colore_interno ||
                                (errors.codice_colore_interno &&
                                  touched.codice_colore_interno)
                                ? 'error-border'
                                : externalWarnings?.codice_colore_interno
                                  ? 'warning-border'
                                  : ''
                            }
                          />
                          {errors.codice_colore_interno &&
                            touched.codice_colore_interno && (
                              <small className="text-danger">
                                {errors.codice_colore_interno as string}
                              </small>
                            )}

                          {externalErrors?.codice_colore_interno && (
                            <small className="text-danger">
                              {externalErrors.codice_colore_interno}
                            </small>
                          )}
                          {externalWarnings?.codice_colore_interno && (
                            <small className="text-warning">
                              {externalWarnings.codice_colore_interno}
                            </small>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('internal_color_description')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.colore_interno}
                            name="colore_interno"
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          {pageMode === 'edit' ? (
                            <>
                              <Form.Label>
                                {t('n_order')}
                                <span className="red-ast">*</span>
                              </Form.Label>
                              <Form.Control
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.nr_ordine}
                                name="nr_ordine"
                                type="text"
                                className={
                                  errors.nr_ordine && touched.nr_ordine
                                    ? 'error-border'
                                    : ''
                                }
                              />
                              {errors.nr_ordine && touched.nr_ordine && (
                                <small className="text-danger">
                                  {errors.nr_ordine as string}
                                </small>
                              )}
                            </>
                          ) : (
                            <>
                              <Form.Label>
                                {t('n_orders')}
                                <span className="red-ast">*</span>
                              </Form.Label>
                              <CreatableSelectExtended
                                onBlur={handleBlur}
                                setFieldTouched={setFieldTouched}
                                setFieldValue={setFieldValue}
                                placeholder={`${i18next.t('press_enter_or_space_to_create')}...`}
                                value={values.nr_ordine_list}
                                name="nr_ordine_list"
                                className={
                                  errors.nr_ordine_list && touched.nr_ordine_list
                                    ? 'error-border'
                                    : ''
                                }
                              ></CreatableSelectExtended>
                              {errors.nr_ordine_list &&
                                touched.nr_ordine_list && (
                                  <small className="text-danger">
                                    {errors.nr_ordine_list as string}
                                  </small>
                                )}
                            </>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('city')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.city}
                            name="city"
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('week_of_production')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="settimana_prod"
                            value={values.settimana_prod}
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('vehicle_registration_date')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.data_immatricolazione}
                            name="data_immatricolazione"
                            type="date"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('expected_availability_date')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="data_disp"
                            value={values.data_disp}
                            type="date"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('vehicle_frame')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.telaio}
                            name="telaio"
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('vehicle_frame_date')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.data_telaio}
                            name="data_telaio"
                            type="date"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('vehicle_plate')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.targa}
                            name="targa"
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('id_bo')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.id_bo}
                            name="id_bo"
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('horizon_availability_day')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.giorno_disp_horizon}
                            name="giorno_disp_horizon"
                            type="date"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('list_price')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.prezzo_listino}
                            name="prezzo_listino"
                            type="number"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('extra_list_price_discount_percentage')}</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.extra_sconto_perc}
                            name="extra_sconto_perc"
                            type="text"
                          />
                          {errors.extra_sconto_perc &&
                            touched.extra_sconto_perc && (
                              <small className="text-danger">
                                {errors.extra_sconto_perc as string}
                              </small>
                            )}
                        </Form.Group>
                      </Col>
                      <VehicleRentalCompanies
                        values={values}
                        touched={touched}
                        errors={errors}
                        setFieldValue={setFieldValue}
                      />
                      <Col md={6} style={{ "display": "flex", "alignItems": "center" }}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="switch"
                            id="promo"
                            label={i18next.t('promo')}
                            name="promo"
                            className="switch"
                            checked={values.promo}
                            onChange={(opt) => {
                              setFieldValue(
                                'promo',
                                opt?.target.checked || false
                              );
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('promo_note')}</Form.Label>
                          <Form.Control
                            as="textarea"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.promo_note}
                            name="promo_note"
                            type="text"
                          />
                        </Form.Group>
                      </Col>

                      <MarketFilter markets={[Markets.it]}>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t('salable_by')}
                              {/* <span className="red-ast">*</span> */}
                            </Form.Label>
                            {pageMode === 'edit' ? (
                              <Select
                                menuPosition="fixed"
                                value={stringToOption(values?.stato_corrente?.visibility_tag)}
                                onBlur={handleBlur}
                                onChange={(opt) => {
                                  setFieldValue('stato_corrente.visibility_tag', opt?.value || null);
                                }}
                                name="stato_corrente.visibility_tag"
                                options={salableByOptions}
                                isClearable={true}
                                className={
                                  'hor-singleselect group-input ' +
                                  (errors.visibility_tag && touched.visibility_tag
                                    ? 'error-border'
                                    : '')
                                }
                              />
                            ) : (
                              <Select
                                menuPosition="fixed"
                                value={stringToOption(values?.visibility_tag)}
                                onBlur={handleBlur}
                                onChange={(opt) => {
                                  setFieldValue('visibility_tag', opt?.value || null);
                                }}
                                name="visibility_tag"
                                options={salableByOptions}
                                isClearable={true}
                                className={
                                  'hor-singleselect group-input ' +
                                  (errors.visibility_tag && touched.visibility_tag
                                    ? 'error-border'
                                    : '')
                                }
                              />
                            )}
                            {errors.visibility_tag && touched.visibility_tag && (
                              <small className="text-danger">
                                {errors.visibility_tag as string}
                              </small>
                            )}
                          </Form.Group>
                        </Col>
                      </MarketFilter>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('generic_notes')}</Form.Label>
                          <Form.Control
                            as="textarea"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.note}
                            name="note"
                            type="text"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <div className="display-7 my-4">{t('optionals')}</div>

                <OptionalsCard
                  handleChange={handleChange}
                  values={values}
                  addOptionDisabled={addOptionDisabled}
                  onOptionAdded={() => {
                    setAddOptionDisabled(true);
                  }}
                />

                <Row>
                  <Col className="mt-4 text-end">
                    <Btn
                      icon="arrow-right"
                      disabled={isSubmitting}
                      onClick={() => {
                        setExternalErrors(null);
                        setExternalWarnings(null);
                        handleSubmit();

                        Object.keys(errors).length &&
                          toaster.error(
                            i18next.t('errors_in_submitted_fields')
                          );
                      }}
                    >
                      {t('save')}
                    </Btn>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      )}
    </Container>
  );
};

export default DetailsFormPage;
